import { Link } from 'react-router-dom';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/utils/cn';

type ActionVariant = 'default' | 'danger' | 'success' | 'warning';

interface ActionButtonProps {
  /** HappyIcon name (without hm- prefix) */
  icon: string;
  /** Tooltip text */
  tooltip: string;
  /** Click handler (for button actions) */
  onClick?: () => void;
  /** Navigation href (renders as Link) */
  href?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Color variant */
  variant?: ActionVariant;
  /** Size */
  size?: 'sm' | 'md';
}

const variantStyles: Record<ActionVariant, string> = {
  default: 'text-primary-700 hover:bg-primary-50 hover:text-primary-900',
  danger: 'text-red-600 hover:bg-red-50 hover:text-red-700',
  success: 'text-green-600 hover:bg-green-50 hover:text-green-700',
  warning: 'text-orange-600 hover:bg-orange-50 hover:text-orange-700',
};

const sizeStyles = {
  sm: 'w-7 h-7 text-sm',
  md: 'w-8 h-8 text-base',
};

/**
 * ActionButton - Icon button with tooltip for admin table actions
 *
 * @example
 * // As a button
 * <ActionButton icon="trash" tooltip="Delete" onClick={handleDelete} variant="danger" />
 *
 * @example
 * // As a link
 * <ActionButton icon="eye" tooltip="View" href={`/admin/users/${id}`} />
 */
export function ActionButton({
  icon,
  tooltip,
  onClick,
  href,
  disabled = false,
  variant = 'default',
  size = 'md',
}: ActionButtonProps) {
  const baseStyles = cn(
    'inline-flex items-center justify-center rounded-lg transition-colors',
    sizeStyles[size],
    disabled
      ? 'opacity-50 cursor-not-allowed text-gray-400'
      : variantStyles[variant]
  );

  const iconElement = <i className={`hm hm-${icon}`} aria-hidden="true" />;

  // Render as Link if href is provided
  if (href && !disabled) {
    return (
      <Tooltip content={tooltip}>
        <Link to={href} className={baseStyles} aria-label={tooltip}>
          {iconElement}
        </Link>
      </Tooltip>
    );
  }

  // Render as button
  return (
    <Tooltip content={tooltip} disabled={disabled}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={baseStyles}
        aria-label={tooltip}
      >
        {iconElement}
      </button>
    </Tooltip>
  );
}

export default ActionButton;
